import React, { useEffect } from 'react';
import { X, Mail, Clock, CheckCircle, XCircle, AlertCircle, FileText, Shield, Zap } from 'lucide-react';
import { trpc } from '../../../lib/trpc';
import { Button, Badge, TimeAgo, LoadingSpinner } from '../../atoms';

interface EmailAttachment {
  id: string;
  filename: string;
  contentType: string;
  size: number;
  processingStatus: string;
  invoicesFound?: number;
  invoicesCreated?: number;
  errorMessage?: string;
}

interface SkippedDuplicate {
  invoiceNumber: string;
  supplier: string;
  reason: string;
  originalInvoice?: {
    id: string;
    invoiceNumber: string;
    supplier: string;
    totalAmount: number;
  };
}

interface DuplicateInvoice {
  invoiceNumber: string;
  supplier: string;
  matchType: string;
  originalInvoice?: {
    id: string;
    invoiceNumber: string;
    supplier: string;
    totalAmount: number;
  };
}

interface EmailDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  emailLogId: string;
}

export const EmailDetailsModal: React.FC<EmailDetailsModalProps> = ({
  isOpen,
  onClose,
  emailLogId
}) => {
  const { data, isLoading, error } = trpc.emailLogs.getEmailLogById.useQuery(
    { id: emailLogId },
    { 
      enabled: isOpen && !!emailLogId,
      staleTime: 60000, // 1 minute - email details don't change frequently
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
    }
  );

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const emailLog = data?.emailLog;
  const attachments = data?.attachments || [];

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'processing':
        return <Clock className="w-5 h-5 text-blue-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-500" />;
    }
  };

  const getVerdictBadge = (verdict: string | null | undefined) => {
    if (!verdict) return <Badge variant="default">Not Available</Badge>;
    
    const isPass = verdict.toLowerCase() === 'pass';
    return (
      <Badge variant={isPass ? 'success' : 'error'}>
        {verdict.toUpperCase()}
      </Badge>
    );
  };

  const formatProcessingTime = (timeMs: number | null | undefined) => {
    if (!timeMs) return 'N/A';
    if (timeMs < 1000) return `${timeMs}ms`;
    return `${(timeMs / 1000).toFixed(2)}s`;
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Mail className="w-6 h-6 text-primary-600" />
            <h2 id="modal-title" className="text-xl font-semibold text-gray-900">
              Email Processing Details
            </h2>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="p-2"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <div id="modal-description" className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {isLoading && (
            <div className="flex justify-center items-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          )}

          {error && (
            <div className="text-center py-8">
              <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <p className="text-red-600 font-medium">Failed to load email details</p>
              <p className="text-gray-600 text-sm mt-2">
                {error.message || 'An unexpected error occurred'}
              </p>
            </div>
          )}

          {emailLog && (
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <Mail className="w-5 h-5 mr-2" />
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">From</label>
                    <p className="text-gray-900">{emailLog.source}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">To</label>
                    <p className="text-gray-900">
                      {Array.isArray(emailLog.destination) 
                        ? emailLog.destination.join(', ') 
                        : emailLog.destination}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Received</label>
                    <p className="text-gray-900">
                      <TimeAgo date={emailLog.emailReceivedAt} />
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Status</label>
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(emailLog.status)}
                      <Badge variant={
                        emailLog.status === 'completed' ? 'success' :
                        emailLog.status === 'failed' ? 'error' :
                        emailLog.status === 'processing' ? 'info' : 'default'
                      }>
                        {emailLog.status}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>

              {/* Processing Results */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <Zap className="w-5 h-5 mr-2" />
                  Processing Results
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {emailLog.invoicesProcessed || 0}
                    </div>
                    <div className="text-sm text-gray-600">Processed</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-yellow-600">
                      {emailLog.invoicesSkipped || 0}
                    </div>
                    <div className="text-sm text-gray-600">Skipped</div>
                    <div className="text-xs text-gray-500">(Duplicates)</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600">
                      {emailLog.invoicesFailed || 0}
                    </div>
                    <div className="text-sm text-gray-600">Failed</div>
                    <div className="text-xs text-gray-500">(Errors)</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {emailLog.attachmentCount || 0}
                    </div>
                    <div className="text-sm text-gray-600">Attachments</div>
                  </div>
                </div>
                
                {/* Detailed Breakdown */}
                {(emailLog.invoicesSkipped > 0 || emailLog.invoicesFailed > 0) && (
                  <div className="mt-4 p-3 bg-white rounded border">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Detailed Breakdown:</h4>
                    <div className="text-sm text-gray-600 space-y-1">
                      {emailLog.invoicesSkipped > 0 && (
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                          <span>
                            <strong>{emailLog.invoicesSkipped}</strong> invoice{emailLog.invoicesSkipped !== 1 ? 's' : ''} skipped as duplicates
                          </span>
                        </div>
                      )}
                      {emailLog.invoicesFailed > 0 && (
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                          <span>
                            <strong>{emailLog.invoicesFailed}</strong> invoice{emailLog.invoicesFailed !== 1 ? 's' : ''} failed processing
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {/* Additional Context for Skipped Invoices */}
                    {emailLog.invoicesSkipped > 0 && (
                      <div className="mt-3 p-2 bg-yellow-50 rounded text-xs text-yellow-700">
                        <strong>Note:</strong> Skipped invoices are typically duplicates that match existing records in the system. 
                        Check the attachment details below for more specific information about which invoices were skipped.
                      </div>
                    )}
                  </div>
                )}
                
                {emailLog.processingTimeMs && (
                  <div className="mt-4 text-center">
                    <span className="text-sm text-gray-600">
                      Processing Time: {formatProcessingTime(emailLog.processingTimeMs)}
                    </span>
                  </div>
                )}
              </div>

              {/* Email Security Verification */}
              <div className="bg-green-50 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <Shield className="w-5 h-5 mr-2" />
                  Email Security Verification
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Spam Check</label>
                    <div className="mt-1">
                      {getVerdictBadge(emailLog.spamVerdict)}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Virus Check</label>
                    <div className="mt-1">
                      {getVerdictBadge(emailLog.virusVerdict)}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">SPF</label>
                    <div className="mt-1">
                      {getVerdictBadge(emailLog.spfVerdict)}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">DKIM</label>
                    <div className="mt-1">
                      {getVerdictBadge(emailLog.dkimVerdict)}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">DMARC</label>
                    <div className="mt-1">
                      {getVerdictBadge(emailLog.dmarcVerdict)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Attachments */}
              {attachments.length > 0 && (
                <div className="bg-purple-50 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <FileText className="w-5 h-5 mr-2" />
                    Attachments ({attachments.length})
                  </h3>
                  <div className="space-y-4">
                    {attachments.map((attachment: EmailAttachment) => (
                      <div key={attachment.id} className="bg-white rounded-lg border p-4">
                        {/* Attachment Header */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <FileText className="w-5 h-5 text-gray-500" />
                            <div>
                              <span className="text-sm font-medium text-gray-900">
                                {attachment.filename || 'Unknown file'}
                              </span>
                              {attachment.contentType && (
                                <span className="text-xs text-gray-500 ml-2">
                                  ({attachment.contentType})
                                </span>
                              )}
                              {attachment.size && (
                                <span className="text-xs text-gray-500 ml-2">
                                  ({Math.round(attachment.size / 1024)}KB)
                                </span>
                              )}
                            </div>
                          </div>
                          <Badge variant={
                            attachment.processingStatus === 'completed' ? 'success' :
                            attachment.processingStatus === 'failed' ? 'error' : 
                            attachment.processingStatus === 'processing' ? 'info' : 'default'
                          }>
                            {attachment.processingStatus}
                          </Badge>
                        </div>

                        {/* Invoice Processing Results */}
                        <div className="grid grid-cols-3 gap-4 mb-3">
                          <div className="text-center">
                            <div className="text-lg font-bold text-blue-600">
                              {attachment.invoicesFound || 0}
                            </div>
                            <div className="text-xs text-gray-600">Found</div>
                          </div>
                          <div className="text-center">
                            <div className="text-lg font-bold text-green-600">
                              {attachment.invoicesCreated || 0}
                            </div>
                            <div className="text-xs text-gray-600">Created</div>
                          </div>
                          <div className="text-center">
                            <div className="text-lg font-bold text-red-600">
                              {(attachment.invoicesFound || 0) - (attachment.invoicesCreated || 0)}
                            </div>
                            <div className="text-xs text-gray-600">Failed/Skipped</div>
                          </div>
                        </div>

                        {/* Error Details */}
                        {attachment.errorMessage && (
                          <div className="mt-3 p-3 bg-red-50 rounded border border-red-200">
                            <div className="flex items-start space-x-2">
                              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                              <div>
                                <div className="text-sm font-medium text-red-800 mb-1">
                                  Processing Error
                                </div>
                                <div className="text-sm text-red-700">
                                  {attachment.errorMessage}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Success Message */}
                        {attachment.processingStatus === 'completed' && 
                         attachment.invoicesCreated > 0 && 
                         !attachment.errorMessage && (
                          <div className="mt-3 p-3 bg-green-50 rounded border border-green-200">
                            <div className="flex items-start space-x-2">
                              <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                              <div className="text-sm text-green-700">
                                Successfully processed {attachment.invoicesCreated} invoice{attachment.invoicesCreated !== 1 ? 's' : ''}
                                {attachment.invoicesFound > attachment.invoicesCreated && 
                                  ` (${attachment.invoicesFound - attachment.invoicesCreated} skipped)`
                                }
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Skipped Invoices Details */}
                        {attachment.invoicesFound > attachment.invoicesCreated && (
                          <div className="mt-3 p-3 bg-yellow-50 rounded border border-yellow-200">
                            <div className="flex items-start space-x-2">
                              <AlertCircle className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                              <div>
                                <div className="text-sm font-medium text-yellow-800 mb-1">
                                  Skipped Invoices ({attachment.invoicesFound - attachment.invoicesCreated})
                                </div>
                                <div className="text-sm text-yellow-700">
                                  {attachment.invoicesFound - attachment.invoicesCreated} invoice{attachment.invoicesFound - attachment.invoicesCreated !== 1 ? 's' : ''} were skipped, likely due to duplicate detection.
                                  {attachment.errorMessage && (
                                    <div className="mt-2 text-xs text-yellow-600">
                                      <strong>Note:</strong> {attachment.errorMessage}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Duplicate Details */}
              {emailLog.errorDetails?.duplicates && (
                <div className="bg-yellow-50 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <AlertCircle className="w-5 h-5 mr-2 text-yellow-500" />
                    Duplicate Invoice Details
                  </h3>
                  
                  {/* Duplicate Summary */}
                  {emailLog.errorDetails.duplicates.summary && (
                    <div className="mb-4 p-3 bg-white rounded border">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Summary:</h4>
                      <div className="text-sm text-gray-600 space-y-1">
                        <div>• <strong>{emailLog.errorDetails.duplicates.summary.totalDuplicatesFound}</strong> duplicate{emailLog.errorDetails.duplicates.summary.totalDuplicatesFound !== 1 ? 's' : ''} detected</div>
                        <div>• <strong>{emailLog.errorDetails.duplicates.summary.duplicatesSkipped}</strong> invoice{emailLog.errorDetails.duplicates.summary.duplicatesSkipped !== 1 ? 's' : ''} skipped</div>
                        {emailLog.errorDetails.duplicates.summary.duplicatesCreatedAnyway > 0 && (
                          <div>• <strong>{emailLog.errorDetails.duplicates.summary.duplicatesCreatedAnyway}</strong> invoice{emailLog.errorDetails.duplicates.summary.duplicatesCreatedAnyway !== 1 ? 's' : ''} created anyway</div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Skipped Duplicates */}
                  {emailLog.errorDetails.duplicates.skippedDuplicates && emailLog.errorDetails.duplicates.skippedDuplicates.length > 0 && (
                    <div className="mb-4 p-3 bg-white rounded border">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Skipped Duplicates:</h4>
                      <div className="space-y-3">
                        {emailLog.errorDetails.duplicates.skippedDuplicates.map((duplicate: SkippedDuplicate, index: number) => (
                          <div key={index} className="p-3 bg-red-50 rounded border border-red-200">
                            <div className="flex items-start space-x-2">
                              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                              <div className="flex-1">
                                <div className="text-sm font-medium text-red-800">
                                  Invoice #{duplicate.invoiceNumber} from {duplicate.supplier}
                                </div>
                                <div className="text-sm text-red-700 mt-1">
                                  {duplicate.reason}
                                </div>
                                {duplicate.originalInvoice && (
                                  <div className="mt-2 text-xs text-red-600">
                                    <div><strong>Original Invoice:</strong> #{duplicate.originalInvoice.invoiceNumber}</div>
                                    <div><strong>Created:</strong> {duplicate.originalInvoice.createdDate}</div>
                                    <div><strong>Match Type:</strong> {duplicate.originalInvoice.matchType}</div>
                                    <div className="mt-1 text-red-500 font-medium">
                                      This invoice number already exists in the system
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Created Duplicates */}
                  {emailLog.errorDetails.duplicates.duplicateInvoices && emailLog.errorDetails.duplicates.duplicateInvoices.length > 0 && (
                    <div className="p-3 bg-white rounded border">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Created But Flagged as Duplicates:</h4>
                      <div className="space-y-3">
                        {emailLog.errorDetails.duplicates.duplicateInvoices.map((duplicate: DuplicateInvoice, index: number) => (
                          <div key={index} className="p-3 bg-yellow-50 rounded border border-yellow-200">
                            <div className="flex items-start space-x-2">
                              <AlertCircle className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                              <div className="flex-1">
                                <div className="text-sm font-medium text-yellow-800">
                                  Invoice #{duplicate.invoiceNumber} from {duplicate.supplier}
                                </div>
                                <div className="text-sm text-yellow-700 mt-1">
                                  {duplicate.warning}
                                </div>
                                {duplicate.originalInvoice && (
                                  <div className="mt-2 text-xs text-yellow-600">
                                    <div><strong>Original Invoice:</strong> #{duplicate.originalInvoice.invoiceNumber}</div>
                                    <div><strong>Created:</strong> {duplicate.originalInvoice.createdDate}</div>
                                    <div><strong>Match Type:</strong> {duplicate.originalInvoice.matchType}</div>
                                    <div className="mt-1 text-yellow-500 font-medium">
                                      This invoice number already exists in the system
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Error Details */}
              {emailLog.errorMessage && (
                <div className="bg-red-50 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <AlertCircle className="w-5 h-5 mr-2 text-red-500" />
                    Overall Processing Error
                  </h3>
                  <div className="bg-white rounded border p-3">
                    <p className="text-red-700 font-medium">{emailLog.errorMessage}</p>
                    {emailLog.errorDetails?.errors && (
                      <div className="mt-3">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Error Details:</h4>
                        <pre className="text-xs text-gray-600 overflow-x-auto bg-gray-50 p-2 rounded border">
                          {JSON.stringify(emailLog.errorDetails.errors, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Technical Metadata */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Technical Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <label className="font-medium text-gray-700">Message ID</label>
                    <p className="text-gray-600 font-mono text-xs break-all">{emailLog.messageId}</p>
                  </div>
                  {emailLog.lambdaRequestId && (
                    <div>
                      <label className="font-medium text-gray-700">Lambda Request ID</label>
                      <p className="text-gray-600 font-mono text-xs break-all">{emailLog.lambdaRequestId}</p>
                    </div>
                  )}
                  {emailLog.region && (
                    <div>
                      <label className="font-medium text-gray-700">Region</label>
                      <p className="text-gray-600">{emailLog.region}</p>
                    </div>
                  )}
                  {emailLog.s3Bucket && (
                    <div>
                      <label className="font-medium text-gray-700">S3 Bucket</label>
                      <p className="text-gray-600">{emailLog.s3Bucket}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200">
          <Button onClick={onClose} variant="outline">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EmailDetailsModal;
